import {createSlice} from "@reduxjs/toolkit";
import { original } from "immer";
import { postData, prePublishPage } from "../service/post";
import { HeaderSchemaVersion, NodeType } from "../types/PageNode";
import { syncAdultBadgesAlongPath, syncAdultNodeInPlace, needsAdultBadgeSync } from "../service/Adult";
import {
    canInsertNodeWithoutDuplicateHeader,
    containsNavigationRegionsNode,
    isHeaderNode,
    isNavigationRegionsNode,
    isNodeInsideHeader,
} from "../utils/nodes/headerNode";

export const nodes = createSlice({
    name: 'nodes',
    initialState: {
        value: [],
        errorMessage: null,
        version: 0,
    },
    reducers: {
        setErrorMessage: (state, action) => {
            state.errorMessage = action.payload;
        },
        addNode: (state, action) => {
            const pageRoot = state.value[0];
            let newNode = action.payload.newNode;
            if (!canInsertNodeWithoutDuplicateHeader(pageRoot, newNode)) return;
            if (containsNavigationRegionsNode(newNode) && !isHeaderNode(newNode)) return;
            if (isNavigationRegionsNode(newNode) && !isNodeInsideHeader(pageRoot, action.payload.activeNode.id)) return;

            let element = isHeaderNode(newNode) ? pageRoot : findNode(pageRoot, action.payload.activeNode.id);
            if (!element || isNavigationRegionsNode(element)) return;
            if (isHeaderNode(element) && element.schemaVersion === HeaderSchemaVersion.Basic && newNode.type !== NodeType.DivRowNode) return;
            // let element = state.value.findLast((element) => element.id === action.payload.activeNode.id);
            newNode.id ??= (Date.now()).toString();
            newNode.classes ??= [];
            newNode.nodes ??= [];
            newNode.parentId = action.payload.activeNode.id;
            // newNode.classes ??= ['border-1', 'border-dashed p-4'];
            if(isHeaderNode(newNode)){
              element.nodes.unshift(action.payload.newNode);
            }else{
              element.nodes.push(action.payload.newNode);
            }
            syncAdultBadgesAlongPath(state.value[0], action.payload.activeNode.id);
            state.version += 1;
        },
        addNavigationItem: (state, action) => {
            const navigationRegion = findNode(state.value[0], action.payload.navigationRegionId);
            const newNode = action.payload.newNode;
            if (!isNavigationRegionsNode(navigationRegion) || newNode?.type !== NodeType.ButtonNode) return;

            newNode.parentId = navigationRegion.id;
            navigationRegion.nodes.push(newNode);
            state.version += 1;
        },
        changeNode: (state, action) => {
            const editedNode = action.payload.editedNode;
            const existingNode = findNode(state.value[0], editedNode.id);
            if (isNavigationRegionsNode(existingNode)) return;
            if (changeNodeByNode(state.value[0], editedNode)) {
                if (needsAdultBadgeSync(existingNode, editedNode)) {
                    syncAdultBadgesAlongPath(state.value[0], editedNode.id);
                }
                state.version += 1;
            }
        },
        changeNodeNoSendRequest: (state, action, sendRequest = true) => {
            const existingNode = findNode(state.value[0], action.payload.editedNode.id);
            if (isNavigationRegionsNode(existingNode)) return;
            const result = changeNodeByNode(state.value[0], action.payload.editedNode);
            if (!result) {
                console.log(action.payload.editedNode)
            } else {
                state.version += 1;
            }
        },
        deleteNode: (state, action) => {
            if (containsNavigationRegionsNode(action.payload.removeNode) && !isHeaderNode(action.payload.removeNode)) return;
            if (deleteNodeByNode(state.value[0], action.payload.removeNode)) {
                state.version += 1;
            }
        },
        moveForward: (state, action) => {
            if (isHeaderNode(action.payload.node)) return;
            if(action.payload.isMobile){
                moveMobileNodeForward(state.value[0], action.payload.node)
            }else{
                moveNodeForward(state.value[0], action.payload.node)
            }
            state.version += 1;
        }, moveBack: (state, action) => {
            if (isHeaderNode(action.payload.node)) return;
            if(action.payload.isMobile){
                moveMobileNodeBack(state.value[0], action.payload.node)
            }else{
                moveNodeBack(state.value[0], action.payload.node)
            }
            state.version += 1;
        },
        reorderChildren: (state, action) => {
            const {parentId, activeId, overId} = action.payload;
            if (reorderChildrenById(state.value[0], parentId, activeId, overId)) {
                syncAdultBadgesAlongPath(state.value[0], parentId);
                state.version += 1;
            }
        },
        reorderChildGroups: (state, action) => {
            const {parentId, activeIds, overIds} = action.payload;
            if (reorderChildGroupsById(state.value[0], parentId, activeIds, overIds)) {
                syncAdultBadgesAlongPath(state.value[0], parentId);
                state.version += 1;
            }
        },
        insertChild: (state, action) => {
            const {parentId, node, index} = action.payload;
            if (insertChildByIndex(state.value[0], parentId, node, index)) {
                syncAdultBadgesAlongPath(state.value[0], parentId);
                state.version += 1;
            }
        },
        insertChildren: (state, action) => {
            const {parentId, children, index} = action.payload;
            if (insertChildrenByIndex(state.value[0], parentId, children, index)) {
                syncAdultBadgesAlongPath(state.value[0], parentId);
                state.version += 1;
            }
        },
        setNodes: (state, action) => {
            state.value = action.payload.nodes;
            if (state.value[0]) {
                syncAdultNodeInPlace(state.value[0]);
            }
            state.version += 1;
        },
        updateRootNode: (state, action) => {
            state.value[0] = action.payload.newRootNode;
            state.version += 1;
        },
        changeNodeHidden: (state, action) => {
            const node = findNode(state.value[0], action.payload.id);
            if (node) {
                if (action.payload.type === 'hidden') {
                    node.hidden = action.payload.hidden;
                } else if (action.payload.type === 'hiddenMobile') {
                    node.hiddenMobile = action.payload.hiddenMobile;
                }
                state.version += 1;
            }
        },
    }
})


/**
 *
 * @param node
 * @param id
 */
export function findNode(node, id) {
    if (node.id === id) {
        return node;
    } else if (node.nodes) {
        let foreachResult = null;
        node.nodes.forEach(function (element) {
            if (element) {
                let tmpElement = findNode(element, id);
                if (tmpElement) {
                    foreachResult = tmpElement;
                    return null;
                }
            }
        });
        return foreachResult;
    }
    return null;
}

/**
 *
 * @param node
 * @param editedNode
 */
function shallowEqual(prevObj, nextObj) {
    if (prevObj === nextObj) return true;
    if (!prevObj || !nextObj || typeof prevObj !== 'object' || typeof nextObj !== 'object') return false;
    const prevKeys = Object.keys(prevObj);
    const nextKeys = Object.keys(nextObj);
    if (prevKeys.length !== nextKeys.length) return false;
    return prevKeys.every(key => prevObj[key] === nextObj[key]);
}

function nodeContentChanged(draftNode, editedNode) {
    const snapshot = original(draftNode);
    if (!snapshot) return true;
    const allKeys = new Set([...Object.keys(snapshot), ...Object.keys(editedNode)]);
    for (const key of allKeys) {
        const prevValue = snapshot[key];
        const newValue = editedNode[key];
        if (prevValue === newValue) continue;
        if (key === 'nodes') {
            const prevChildren = prevValue ?? [];
            const nextChildren = newValue ?? [];
            if (prevChildren.length !== nextChildren.length) return true;
            if (prevChildren.some((child, i) => child?.id !== nextChildren[i]?.id)) return true;
            continue;
        }

        if (prevValue && newValue && typeof prevValue === 'object' && typeof newValue === 'object') {
            if (!shallowEqual(prevValue, newValue)) return true;
        } else {
            return true;
        }
    }
    return false;
}

function changeNodeByNode(node, editedNode) {
    if (node.id === editedNode.id) {
        if (!nodeContentChanged(node, editedNode)) return false;
        Object.assign(node, editedNode);
        return true;
    }
    if (node.nodes.length > 0) {
        let searchIndex = false;
        node.nodes.forEach(function (element, index) {
            if (element) {
                if (element.id === editedNode.id) {
                    searchIndex = index;
                    return null;
                }
            }
        });
        if (searchIndex !== false) {
            if (!nodeContentChanged(node.nodes[searchIndex], editedNode)) return false;
            node.nodes[searchIndex] = editedNode;
            return true;
        }
        for (const element of node.nodes) {
            if (element && changeNodeByNode(element, editedNode)) {
                return true;
            }
        }
        return false;
    }
}

/**
 *
 * @param node
 * @param removeNode
 */
function deleteNodeByNode(node, removeNode) {
    if (!node?.nodes?.length || !removeNode) return false;

    const searchIndex = node.nodes.findIndex((element) => element?.id === removeNode.id);
    if (searchIndex > -1) {
        node.nodes.splice(searchIndex, 1);
        return true;
    }

    for (const element of node.nodes) {
        if (element && deleteNodeByNode(element, removeNode)) {
            return true;
        }
    }

    return false;
}

/**
 *
 * @param node
 * @param nodeToAction
 */

function clearMobileOrder(nodes) {
    if (!nodes?.length) return;
    nodes.forEach(node => {
      if (node.customStyleMobile && 'order' in node.customStyleMobile) {
        const updatedStyle = { ...node.customStyleMobile };
        delete updatedStyle.order;
        node.customStyleMobile = updatedStyle;
      }
    });
  }

export function reorderChildrenById(rootNode, parentId, activeId, overId) {
    if (!rootNode || !parentId || !activeId || !overId || activeId === overId) {
        return false;
    }

    const parentNode = findNode(rootNode, parentId);
    if (!parentNode?.nodes?.length) return false;

    const activeIndex = parentNode.nodes.findIndex((node) => node?.id === activeId);
    const overIndex = parentNode.nodes.findIndex((node) => node?.id === overId);
    if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) return false;

    const [activeNode] = parentNode.nodes.splice(activeIndex, 1);
    parentNode.nodes.splice(overIndex, 0, activeNode);
    clearMobileOrder(parentNode.nodes);
    return true;
}

export function reorderChildGroupsById(rootNode, parentId, activeIds, overIds) {
    if (!rootNode || !parentId || !activeIds?.length || !overIds?.length) return false;

    const parentNode = findNode(rootNode, parentId);
    if (!parentNode?.nodes?.length) return false;

    const activeIdSet = new Set(activeIds);
    const overIdSet = new Set(overIds);
    if (activeIds.some((id) => overIdSet.has(id))) return false;

    const activeNodes = parentNode.nodes.filter((node) => activeIdSet.has(node?.id));
    const overNodes = parentNode.nodes.filter((node) => overIdSet.has(node?.id));
    if (activeNodes.length !== activeIdSet.size || overNodes.length !== overIdSet.size) {
        return false;
    }

    const activeStart = parentNode.nodes.findIndex((node) => activeIdSet.has(node?.id));
    const overStart = parentNode.nodes.findIndex((node) => overIdSet.has(node?.id));
    const remainingNodes = parentNode.nodes.filter((node) => !activeIdSet.has(node?.id));
    const remainingOverIndexes = remainingNodes
        .map((node, index) => overIdSet.has(node?.id) ? index : -1)
        .filter((index) => index >= 0);
    const insertionIndex = activeStart < overStart
        ? Math.max(...remainingOverIndexes) + 1
        : Math.min(...remainingOverIndexes);

    remainingNodes.splice(insertionIndex, 0, ...activeNodes);
    parentNode.nodes = remainingNodes;
    clearMobileOrder(parentNode.nodes);
    return true;
}

function collectNodeIds(node, ids = []) {
    if (!node) return ids;
    ids.push(node.id);
    node.nodes?.forEach((childNode) => collectNodeIds(childNode, ids));
    return ids;
}

export function insertChildByIndex(rootNode, parentId, nodeToInsert, index) {
    return insertChildrenByIndex(rootNode, parentId, [nodeToInsert], index);
}

export function insertChildrenByIndex(rootNode, parentId, nodesToInsert, index) {
    if (!rootNode || !parentId || !Array.isArray(nodesToInsert) || !nodesToInsert.length) {
        return false;
    }

    if (nodesToInsert.some((node) => !node?.id)) return false;

    const parentNode = findNode(rootNode, parentId);
    if (!parentNode?.nodes) return false;

    const insertedIds = nodesToInsert.flatMap((node) => collectNodeIds(node));
    if (insertedIds.some((id) => !id)) return false;
    if (new Set(insertedIds).size !== insertedIds.length) return false;
    if (insertedIds.some((id) => findNode(rootNode, id))) return false;

    nodesToInsert.forEach((node) => {
        node.parentId = parentId;
    });
    const insertionIndex = Number.isInteger(index)
        ? Math.max(0, Math.min(index, parentNode.nodes.length))
        : parentNode.nodes.length;

    parentNode.nodes.splice(insertionIndex, 0, ...nodesToInsert);
    clearMobileOrder(parentNode.nodes);
    return true;
}
  
  function moveNodeForward(node, nodeToAction) {
    if (node.nodes.length > 0) {
      let searchIndex = -1;
      node.nodes.forEach((element, index) => {
        if (element !== null && element.id === nodeToAction.id) {
          searchIndex = index;
          return null;
        }
      });
  
      if (searchIndex > -1 && node.nodes[searchIndex + 1] !== undefined) {
        const tmp = node.nodes[searchIndex];
        node.nodes[searchIndex] = node.nodes[searchIndex + 1];
        node.nodes[searchIndex + 1] = tmp;
  
        clearMobileOrder(node.nodes);
        return true;
      } else if (searchIndex === node.nodes.length - 1) {
        const tmp = node.nodes[searchIndex];
        node.nodes.splice(searchIndex, 1);
        const insertionIndex = isHeaderNode(node.nodes[0]) ? 1 : 0;
        node.nodes.splice(insertionIndex, 0, tmp);
  
        clearMobileOrder(node.nodes);
        return true;
      } else {
        node.nodes.forEach((element) => {
          if (element !== null) {
            if (moveNodeForward(element, nodeToAction)) return true;
          }
        });
      }
  
      return false;
    }
  }
  
  

/**
 *
 * @param node
 * @param nodeToAction
 */
function moveNodeBack(node, nodeToAction) {
    if (node.nodes.length > 0) {
      let searchIndex = -1;
      node.nodes.forEach((element, index) => {
        if (element !== null && element.id === nodeToAction.id) {
          searchIndex = index;
          return null;
        }
      });
  
      if (searchIndex > 0 && !isHeaderNode(node.nodes[searchIndex - 1])) {
        const tmp = node.nodes[searchIndex];
        node.nodes[searchIndex] = node.nodes[searchIndex - 1];
        node.nodes[searchIndex - 1] = tmp;
  
        clearMobileOrder(node.nodes);
        return true;
      } else {
        node.nodes.forEach((element) => {
          if (element !== null) {
            if (moveNodeBack(element, nodeToAction)) return true;
          }
        });
      }
  
      return false;
    }
  }
  
function ensureGridColumn(parentNode) {
    const prev = parentNode.customStyleMobile || {};
    const isRow = typeof parentNode.className === 'string'
      && parentNode.className.includes('row');
    
    const next = {
      ...prev,
      display: isRow ? 'flex' : 'grid',
    
      ...(isRow
        ? { flexDirection: 'row' }
        : {
            gridTemplateColumns: '1fr',
            gridAutoRows: 'auto',
            gridAutoFlow: 'row',
          }
      ),
    };
    
    changeNodeByNode(parentNode, {
      ...parentNode,
      customStyleMobile: next,
    });
  }

  
  function initOrder(nodes) {
    if (!nodes?.length) return;
    nodes.forEach((node, idx) => {
      if (node.customStyleMobile?.order == null) {
        changeNodeByNode(node, {
          ...node,
          customStyleMobile: {
            ...node.customStyleMobile,
            order: idx
          }
        });
      }
    });
  }

  
  function moveMobileNodeForward(rootNode, nodeToAction) {
    const parentNode = findNode(rootNode, nodeToAction.parentId);
    if (!parentNode) return;
  
    ensureGridColumn(parentNode);
    initOrder(parentNode.nodes);
  
    const sorted = [...parentNode.nodes].sort(
      (a, b) => a.customStyleMobile.order - b.customStyleMobile.order
    );

    const pos = sorted.findIndex(n => n.id === nodeToAction.id);
    if (pos > -1 && pos < sorted.length - 1) {
      [sorted[pos], sorted[pos + 1]] = [sorted[pos + 1], sorted[pos]];
      // 4) Переписываем order у всех
      sorted.forEach((node, idx) => {
        changeNodeByNode(node, {
          ...node,
          customStyleMobile: {
            ...node.customStyleMobile,
            order: idx
          }
        });
      });
    }
  }
  
  function moveMobileNodeBack(rootNode, nodeToAction) {
    const parentNode = findNode(rootNode, nodeToAction.parentId);
    if (!parentNode) return;
  
    ensureGridColumn(parentNode);
    initOrder(parentNode.nodes);
  
    const sorted = [...parentNode.nodes].sort(
      (a, b) => a.customStyleMobile.order - b.customStyleMobile.order
    );
    const pos = sorted.findIndex(n => n.id === nodeToAction.id);
    if (pos > 0 && !isHeaderNode(sorted[pos - 1])) {
      [sorted[pos - 1], sorted[pos]] = [sorted[pos], sorted[pos - 1]];
      sorted.forEach((node, idx) => {
        changeNodeByNode(node, {
          ...node,
          customStyleMobile: {
            ...node.customStyleMobile,
            order: idx
          }
        });
      });
    }
  }
  
export const {addNode} = nodes.actions
export const {addNavigationItem} = nodes.actions
export const {changeNode} = nodes.actions
export const {changeNodeNoSendRequest} = nodes.actions
export const {deleteNode} = nodes.actions
export const {moveForward} = nodes.actions
export const {moveBack} = nodes.actions
export const {reorderChildren} = nodes.actions
export const {reorderChildGroups} = nodes.actions
export const {insertChild} = nodes.actions
export const {insertChildren} = nodes.actions
export const {setNodes} = nodes.actions
export const {updateRootNode} = nodes.actions;
export const {changeNodeHidden} = nodes.actions
export const {setErrorMessage} = nodes.actions
export const {returnToDesktopMovement} = nodes.actions
export const {returnToMobileMovement} = nodes.actions



export default nodes.reducer;

let prePublishTimeout;
let isSaving = false;
let pendingSave = null;

async function runSave(pageId, nodes, token, store) {
    isSaving = true;
    try {
        token
            ? await postData(process.env.REACT_APP_BACKEND_ADDRESS + "/page/save/" + pageId, nodes)
            : await prePublishPage(+pageId, nodes);
    } catch (error) {
        store.dispatch(setErrorMessage(error.message));
    } finally {
        isSaving = false;
        if (pendingSave) {
            const next = pendingSave;
            pendingSave = null;
            runSave(next.pageId, next.nodes, next.token, store);
        }
    }
}

export const nodesMiddleware = (store) => (next) => async (action) => {
    const prevVersion = store.getState().nodes.present.version;
    const result = next(action);
    const state = store.getState().nodes;
    const queryString = window.location.search;
    const urlSearchParams = new URLSearchParams(queryString);
    const token = urlSearchParams.get("token");

    const nodeArraysAreEqual = prevVersion === state.present.version;

    const shouldSave =
        action.type === addNode.type ||
        action.type === addNavigationItem.type ||
        (action.type === changeNode.type && !nodeArraysAreEqual) ||
        (action.type === deleteNode.type && !nodeArraysAreEqual) ||
        action.type === moveForward.type ||
        action.type === moveBack.type ||
        (action.type === reorderChildren.type && !nodeArraysAreEqual) ||
        (action.type === reorderChildGroups.type && !nodeArraysAreEqual) ||
        (action.type === insertChild.type && !nodeArraysAreEqual) ||
        (action.type === insertChildren.type && !nodeArraysAreEqual) ||
        action.type === updateRootNode.type ||
        action.type === changeNodeHidden.type;

    if (shouldSave) {
        clearTimeout(prePublishTimeout);

        const saveCurrentState = () => {
            const pageId = store.getState().appStorage.pageId;
            const nodes = store.getState().nodes.present.value;
            if (isSaving) {
                pendingSave = { pageId, nodes, token };
            } else {
                runSave(pageId, nodes, token, store);
            }
        };

        if (action.type === deleteNode.type) {
            saveCurrentState();
            return result;
        }

        prePublishTimeout = setTimeout(saveCurrentState, 500);
    }

    return result;
};
